import Button, {ButtonSize} from "@components/Button";
import {Tooltip} from "@components/components/Tooltip";
import Icon from "@components/Icon";
import {PhoneInput, TextInput} from "@components/Input";
import {useFormErrorMessages} from "@components/Input/utils/errorMessages";
import {formatDate} from "@components/Input/utils/InputFormatter/date";
import {required, validateDate, validatePhoneNumber} from "@components/Input/utils/Validators";
import {useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {getEmail} from "@page-containers/booking/utils/emailStorage";
import {useBookAndGoToConfirmation} from "@page-containers/booking/utils/useBookAndGoToConfirmation";
import {useSelectedDoctor} from "@page-containers/booking/utils/useSelectedDoctor";
import {getMilesAway} from "@page-containers/booking/utils/webBookingAnalytics";
import {MonolithLocationFields} from "@services/graphql/queries/location";
import {Gender, createPatient} from "@services/monolith/patients";
import {getCountryCodeForRegionCode} from "awesome-phonenumber";
import Link from "next/link";
import {Trans, useTranslation} from "@i18n/client";
import React, {MouseEvent, useCallback, useEffect, useMemo, useState} from "react";
import {useSexAtBirthText} from "src/hooks/booking/useSexAtBirthText";
import {I18nNamespace} from "@i18n";
import useMediaQuery from "src/useMediaQuery";
import {EventType} from "src/utils/analytics/constants";
import {useGeolocation} from "src/utils/browser-storage/location";
import {v4} from "uuid";

import {login} from "../../../../utils/browser-storage/tokens";
import {AUTO_FILL_FORMS, v5Pages} from "../../../_common/_constants";
import {formatPhoneNumber} from "../../../_common/Carbon";
import {AppointmentTile} from "../components/AppointmentTile";
import Heading from "../Heading";
import {postBookItEvent, postInputFieldEvent, postViewEvent} from "./events";
import {usePostBookingProgressMutation} from "@page-containers/booking/utils/bookingProgress/useBookingProgressMutation";
import {PatientInfo} from "@page-containers/booking/utils/bookingProgress/types";

type Props = {
  location: MonolithLocationFields;
  time: number;
  apptReasonId: string;
  openSexAtBirthBottomSheet: () => void;
  specialtyId: string;
  prefilledData?: PatientInfo;
  dropoutVisitorId?: string;
};

const AppointmentFor: React.FC<Props> = ({
  apptReasonId,
  location,
  time,
  openSexAtBirthBottomSheet,
  specialtyId,
  prefilledData,
  dropoutVisitorId,
}) => {
  const {selectedSlotIds, selectedTime} = useParsedBookingQuery();

  const [viewId] = useState(v4());
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const email = getEmail();
  const userPosition = useGeolocation();
  const isSm = useMediaQuery("sm");
  const sexAtBirthText = useSexAtBirthText();

  const [firstName, setFirstName] = useState<string>(prefilledData?.firstName || "");
  const [lastName, setLastName] = useState<string>(prefilledData?.lastName || "");
  const [dateOB, setDateOB] = useState<string>(prefilledData?.dob || "");
  const [phoneNumber, setPhoneNumber] = useState<string>(prefilledData?.phoneNumber || "");
  const [region, setRegion] = useState<string>(prefilledData?.phoneNumberCountry || "US");
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState<Gender | "">(
    prefilledData?.sexAtBirth || "",
  );

  const [validateFirstName, setValidateFirstName] = useState<boolean>(!!prefilledData?.firstName);
  const [validateLastName, setValidateLastName] = useState<boolean>(!!prefilledData?.lastName);
  const [validateDateOB, setValidateDateOB] = useState<boolean>(!!prefilledData?.dob);
  const [validatePNumber, setValidatePhoneNumber] = useState<boolean>(!!prefilledData?.phoneNumber);

  const openBottomSheet = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      openSexAtBirthBottomSheet();
    },
    [openSexAtBirthBottomSheet],
  );

  const {
    requiredDate,
    invalidDate,
    futureDate,
    tooYoungDate,
    invalidPhoneNumber,
    requiredFirstName,
    requiredLastName,
    requiredPhoneNumber,
  } = useFormErrorMessages(formatPhoneNumber(location.phoneNumber || ""));

  const phoneValidator = useCallback(validatePhoneNumber(invalidPhoneNumber), [
    validatePhoneNumber,
    invalidPhoneNumber,
  ]);

  const dateValidator = useCallback(
    validateDate([requiredDate, invalidDate, futureDate, tooYoungDate]),
    [validateDate, requiredDate, invalidDate, futureDate],
  );

  const errorMsg = useMemo(
    () => ({
      firstName: validateFirstName ? required(requiredFirstName)(firstName) : undefined,
      lastName: validateLastName ? required(requiredLastName)(lastName) : undefined,
      dateOB: validateDateOB ? required(requiredDate)(dateOB) || dateValidator(dateOB) : undefined,
      phoneNumber: validatePNumber
        ? required(requiredPhoneNumber)(phoneNumber) || phoneValidator(phoneNumber, region)
        : undefined,
    }),
    [
      firstName,
      lastName,
      dateOB,
      phoneNumber,
      region,
      validateFirstName,
      validateLastName,
      validateDateOB,
      validatePNumber,
      requiredFirstName,
      requiredLastName,
      requiredPhoneNumber,
      requiredDate,
      phoneValidator,
      dateValidator,
    ],
  );

  const numberOfValidatedFields = useMemo(
    () =>
      [validateFirstName, validateLastName, validateDateOB, validatePNumber].filter(
        fieldValid => fieldValid === true,
      ).length,
    [validateDateOB, validateFirstName, validateLastName, validatePNumber],
  );

  const [postBookingProgress] = usePostBookingProgressMutation(dropoutVisitorId);

  const _firstName = validateFirstName && !errorMsg.firstName ? firstName : undefined;
  const _lastName = validateLastName && !errorMsg.lastName ? lastName : undefined;
  const _dob = validateDateOB && !errorMsg.dateOB ? dateOB : undefined;
  const _phoneNumber = validatePNumber && !errorMsg.phoneNumber ? phoneNumber : undefined;

  useEffect(() => {
    postBookingProgress({
      patientInfo: {
        email,
        firstName: _firstName,
        lastName: _lastName,
        dob: _dob,
        phoneNumber: _phoneNumber,
        phoneNumberCountry: region,
        sexAtBirth: sexAssignedAtBirth || undefined,
      },
    });
  }, [
    postBookingProgress,
    region,
    sexAssignedAtBirth,
    email,
    _firstName,
    _lastName,
    _dob,
    _phoneNumber,
  ]);

  const numberOfFilledFields = useMemo(
    () =>
      [
        firstName.length,
        lastName.length,
        dateOB.length,
        phoneNumber.length,
        sexAssignedAtBirth.length,
      ].filter(input => input > 0).length,
    [dateOB.length, firstName.length, lastName.length, phoneNumber.length, sexAssignedAtBirth],
  );

  const isformValidated = useMemo(
    () => numberOfValidatedFields >= 3 && numberOfFilledFields === 5,
    [numberOfValidatedFields, numberOfFilledFields],
  );

  const isFormValid = useMemo(
    () => Object.values(errorMsg).every(val => typeof val !== "string"),
    [errorMsg],
  );

  const bookAndGoToConfirmation = useBookAndGoToConfirmation(undefined, dropoutVisitorId);

  // No "useCallback" here because "selectedSlotIds" is an array from the query param,
  // so it will make a new instance every render anyway.
  const handleSubmit = async (e: MouseEvent) => {
    e.preventDefault();

    if (email && isFormValid && selectedSlotIds && sexAssignedAtBirth) {
      emitBookItEvent();

      const {patientId, userId, userToken} = await createPatient({
        phoneNumber: formatPhone(region, phoneNumber),
        phoneNumberCountry: region,
        birthdate: formatDateOB(dateOB),
        firstName,
        lastName,
        email,
        gender: sexAssignedAtBirth,
      });
      await login(userToken, userId);

      bookAndGoToConfirmation(
        {
          patientId,
          slotIds: selectedSlotIds,
          locationId: location.id,
          apptReasonId,
          specialtyId,
          virtual: false,
          practiceId: location.practiceId,
        },
        {
          locationSlug: location.slug,
          selectedTime,
        },
      );
    }
  };

  const emitFirstNameEvent = useCallback(() => {
    if (firstName)
      postInputFieldEvent({viewId, typeId: EventType.FirstNameTyped, value: firstName});
  }, [firstName, viewId]);

  const emitLastNameEvent = useCallback(() => {
    if (lastName) postInputFieldEvent({viewId, typeId: EventType.LastNameTyped, value: lastName});
  }, [lastName, viewId]);

  const emitDateOBEvent = useCallback(() => {
    if (dateOB) postInputFieldEvent({viewId, typeId: EventType.DateofBirthTyped, value: dateOB});
  }, [dateOB, viewId]);

  const emitPhoneNumberEvent = useCallback(() => {
    if (phoneNumber)
      postInputFieldEvent({viewId, typeId: EventType.PhoneNumberTyped, value: phoneNumber});
  }, [phoneNumber, viewId]);

  const bookingButtonText = t("Book it", {ns: "web-scheduling"});

  const emitBookItEvent = useCallback(
    () =>
      postBookItEvent({
        value: bookingButtonText,
        viewId,
      }),
    [bookingButtonText, viewId],
  );

  const emitViewEvent = useCallback(() => {
    postViewEvent({
      viewId,
      apptReasonId,
      locationId: location.id,
      slotId: selectedSlotIds?.[0],
      milesAway: userPosition && location ? getMilesAway(userPosition, location) : undefined,
      timeToAppt: selectedTime ? selectedTime - Date.now() : undefined,
      specialtyId,
    });
  }, [apptReasonId, location, selectedSlotIds, selectedTime, specialtyId, userPosition, viewId]);

  const onDateOBChange = useCallback(
    (value: string) => {
      setDateOB(oldDateOB => formatDate(oldDateOB, value));
    },
    [setDateOB],
  );

  useEffect(() => {
    if (userPosition) emitViewEvent();
  }, [emitViewEvent, userPosition]);

  const termsOfService = useCallback(() => {
    return (
      <Trans ns="web-scheduling">
        <span className="text-center text-sm text-gray-800 font-ir mt-6">
          <span>By scheduling an appointment you agree to our </span>
          <Link className="underline" href={{pathname: v5Pages.terms}}>
            Terms of Service
          </Link>
          <span> and </span>
          <Link className="underline" href={{pathname: v5Pages.privacy}}>
            Privacy Policy
          </Link>
          <span>.</span>
        </span>
      </Trans>
    );
  }, []);

  const dob = useCallback(
    () => (
      <div className="pb-4">
        <TextInput
          label={t("Date of Birth", {ns: "web-scheduling"})}
          ariaLabel={"patient-date-of-birth"}
          className="w-full"
          onChange={onDateOBChange}
          placeholder={"MM/DD/YYYY"}
          errorMsg={errorMsg.dateOB}
          setValidation={setValidateDateOB}
          value={AUTO_FILL_FORMS ? "01/01/198" : dateOB}
          onBlur={emitDateOBEvent}
          onEnter={emitDateOBEvent}
          data-cy="patient-date-of-birth"
          inputMode="numeric"
        />
      </div>
    ),
    [onDateOBChange, setValidateDateOB, emitDateOBEvent, dateOB, errorMsg, t],
  );

  const onBirthSexClick = useCallback(
    (sex: Gender, e: MouseEvent) => {
      e.preventDefault();
      setSexAssignedAtBirth(sex);
      postInputFieldEvent({viewId, typeId: EventType.SexAssignedAtBirthSelected, value: sex});
    },
    [viewId],
  );

  const LabelWrapper = ({children}: {children: React.ReactNode}) => {
    if (isSm) {
      return (
        <button onClick={openBottomSheet} className="pl-2 bg-transparent border-none" aria-hidden>
          {children}
        </button>
      );
    }

    return (
      <Tooltip title={sexAtBirthText} arrow placement="top-start">
        {children}
      </Tooltip>
    );
  };

  const getButtonClassName = useCallback((isSelected: boolean) => {
    const baseClassName =
      "border border-solid border-gray-300 font-i cursor-pointer text-sm sm:text-base leading-none px-6 py-4 sm:py-5 rounded-full w-full my-2 mr-2";
    return isSelected
      ? `${baseClassName} text-gray-1000 border-yellow-200 bg-yellow-200 border-none font-bold`
      : `${baseClassName} text-gray-800 bg-transparent`;
  }, []);

  const doctor = useSelectedDoctor();

  return (
    <>
      <div className="flex justify-center">
        <div className="flex flex-col items-center">
          <AppointmentTile
            firstName={doctor?.firstName || undefined}
            lastName={doctor?.lastName || undefined}
            credential={doctor?.credential || undefined}
            imageId={doctor?.coverImage?.id || location.coverImageId}
            locationTimezone={location.timezone}
            street={location.address.firstLine}
            city={location.address.city}
            time={time}
          />
          <Heading className="mt-8 text-center">
            {t("Who is the appointment for?", {ns: "web-scheduling"})}
          </Heading>
          <form className="flex flex-col w-full mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row pb-4">
              <div className="w-full sm:w-1/2 sm:pr-2 pb-4 sm:pb-0">
                <TextInput
                  label={t("Patient First Name", {ns: "web-scheduling"})}
                  ariaLabel={"patient-first-name"}
                  className="w-full"
                  onChange={setFirstName}
                  errorMsg={errorMsg.firstName}
                  setValidation={setValidateFirstName}
                  onBlur={emitFirstNameEvent}
                  onEnter={emitFirstNameEvent}
                  value={firstName}
                  data-cy="patient-first-name"
                  inputMode="text"
                />
              </div>
              <div className="w-full sm:w-1/2 sm:pl-2">
                <TextInput
                  label={t("Patient Last Name", {ns: "web-scheduling"})}
                  ariaLabel={"patient-last-name"}
                  className="w-full"
                  onChange={setLastName}
                  errorMsg={errorMsg.lastName}
                  setValidation={setValidateLastName}
                  onBlur={emitLastNameEvent}
                  onEnter={emitLastNameEvent}
                  value={lastName}
                  data-cy="patient-last-name"
                  inputMode="text"
                />
              </div>
            </div>
            {isSm && dob()}
            <div className="flex flex-row">
              <PhoneInput
                phoneNumber={phoneNumber}
                handlePhoneChange={setPhoneNumber}
                setValidationPN={setValidatePhoneNumber}
                region={region}
                handleRegionChange={setRegion}
                phoneErrorMsg={errorMsg.phoneNumber}
                onBlur={emitPhoneNumberEvent}
                onEnter={emitPhoneNumberEvent}
                data-cy="patient-phone-number"
              />
              {!isSm && dob()}
            </div>
            <div className="flex flex-col sm:flex-row pb-2">
              <div className="w-full sm:w-1/3 sm:pl-2 py-2 self-center">
                <LabelWrapper>
                  <label id="sex-assigned-at-birth-question" className="font-i text-gray-700">
                    {t("Sex Assigned at Birth", {ns: "web-scheduling"})}
                    <Icon icon="inf" className="text-sm pl-2" />
                  </label>
                </LabelWrapper>
              </div>
              <div className="w-full sm:w-2/3 sm:pl-2">
                <div
                  className="flex flex-row"
                  role="radiogroup"
                  aria-required
                  aria-labelledby="sex-assigned-at-birth-question"
                  aria-details={sexAtBirthText}
                >
                  <button
                    role="radio"
                    aria-checked={sexAssignedAtBirth === Gender.Male}
                    className={getButtonClassName(sexAssignedAtBirth === Gender.Male)}
                    onClick={e => onBirthSexClick(Gender.Male, e)}
                    data-cy="patient-sex-at-birth-male"
                  >
                    {t("Male", {ns: "web-scheduling"})}
                  </button>
                  <button
                    role="radio"
                    aria-checked={sexAssignedAtBirth === Gender.Female}
                    className={getButtonClassName(sexAssignedAtBirth === Gender.Female)}
                    onClick={e => onBirthSexClick(Gender.Female, e)}
                    data-cy="patient-sex-at-birth-female"
                  >
                    {t("Female", {ns: "web-scheduling"})}
                  </button>
                  <button
                    role="radio"
                    aria-checked={sexAssignedAtBirth === Gender.Unknown}
                    className={getButtonClassName(sexAssignedAtBirth === Gender.Unknown)}
                    onClick={e => onBirthSexClick(Gender.Unknown, e)}
                    data-cy="patient-sex-at-birth-other"
                  >
                    {t("Unknown", {ns: "web-scheduling"})}
                  </button>
                </div>
              </div>
            </div>
            {isSm && termsOfService()}
            <Button
              className="w-full mt-6"
              onClick={handleSubmit}
              size={ButtonSize.LG}
              disabled={!isformValidated || !isFormValid}
              data-cy="patient-submit"
            >
              {bookingButtonText}
            </Button>
            {!isSm && termsOfService()}
          </form>
        </div>
      </div>
    </>
  );
};

export default React.memo(AppointmentFor);

const NON_DIGIT = /\D/g;
const formatPhone = (region: string, phoneNumber: string) => {
  const code = getCountryCodeForRegionCode(region);
  return `${code}${phoneNumber.replaceAll(NON_DIGIT, "")}`;
};

export const formatDateOB = (dateOB: string) => {
  const split = dateOB.split("/");
  return `${split[2]}-${split[0]}-${split[1]}`;
};
